// @spec AI-STR-011
// Policy scan detail/results page

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { usePolicyScan, ScanProgress, ScanResults } from '@/features/ai/scans';

export const Route = createFileRoute('/_authenticated/ai/scans/$scanId')({
  component: ScanDetailPage,
});

function ScanDetailPage() {
  const { t } = useTranslation('common');
  const { scanId } = Route.useParams();
  const navigate = useNavigate();

  const { data: scan, isLoading, error, refetch } = usePolicyScan(scanId);

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-5xl">
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">{t('common:routeErrors.loadingScan')}</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-5xl">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-sm text-destructive">{error.message}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                void refetch();
              }}
              className="mt-4"
            >
              Try again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!scan) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-5xl">
          <div className="text-center py-12">
            <p className="text-muted-foreground">Scan not found</p>
          </div>
        </div>
      </div>
    );
  }

  const isProcessing = scan.status === 'pending' || scan.status === 'processing';
  const isCompleted = scan.status === 'completed';
  const isFailed = scan.status === 'failed';

  return (
    <div className="p-8">
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void navigate({ to: '/ai/scans' });
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Scans
          </Button>
        </div>

        <div className="mb-8">
          <h1 className="text-2xl font-semibold">{scan.documentTitle}</h1>
          <p className="mt-2 text-muted-foreground">{scan.regulationName} Compliance Analysis</p>
        </div>

        {/* Progress or Results */}
        {isProcessing && (
          <ScanProgress
            status={scan.status}
            regulation={scan.regulation}
            documentTitle={scan.documentTitle}
            estimatedSeconds={45}
          />
        )}

        {isFailed && (
          <ScanProgress
            status={scan.status}
            regulation={scan.regulation}
            documentTitle={scan.documentTitle}
            errorMessage={scan.errorMessage}
            onRetry={() => {
              // TODO: Implement retry functionality
              void refetch();
            }}
          />
        )}

        {isCompleted && (
          <ScanResults
            scan={scan}
            onExport={(format) => {
              // TODO: Implement export functionality
              console.log('Export requested:', format);
            }}
          />
        )}
      </div>
    </div>
  );
}
